/**
 * WVB-02 — Financial Mini Panel (ADR-B09)
 * Condensed financial overview embedded as the 2nd tab in EventMasterWorkspace.
 * Shows: top 4 KPIs, budget status, quick-create, navigation to full workspace.
 * Keeps EventMasterWorkspace.jsx small (delegated component pattern).
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ExternalLink, Plus, AlertCircle, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { budgetApi, hasCapability } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import FinKpiCard from "./FinKpiCard";
import { BUDGET_STATUS_CONFIG } from "./EventFinancialsUtils";

export default function EventFinancialsMiniPanel({ eid }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = hasCapability(user, "budget.edit");

  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await budgetApi.getFinancials(eid);
      setFinancials(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load financial data");
    } finally {
      setLoading(false);
    }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  async function handleCreate() {
    setCreating(true);
    try {
      await budgetApi.createBudget(eid, { currency_code: "INR" });
      toast.success("Budget created — opening financial workspace");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create budget");
    } finally { setCreating(false); }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-600 justify-center py-12" data-testid="fin-panel-error">
        <AlertCircle className="w-5 h-5" /> {error}
      </div>
    );
  }

  const topKpis = [
    financials?.contracted_revenue,
    financials?.internal_cost_budget,
    financials?.forecast_gross_margin,
    financials?.forecast_gross_profit,
  ].filter(Boolean);

  const statusCfg = financials?.budget_status
    ? BUDGET_STATUS_CONFIG[financials.budget_status]
    : null;

  return (
    <div className="space-y-5" data-testid="financials-mini-panel">

      {/* Panel header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-indigo-500" />
          {financials?.has_budget ? (
            <>
              <span className="text-sm font-semibold text-slate-700">{financials.budget_number}</span>
              <span className="text-xs text-slate-400">v{financials.budget_version}</span>
              {statusCfg && (
                <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${statusCfg.cls}`}>
                  {statusCfg.label}
                </span>
              )}
            </>
          ) : (
            <span className="text-sm text-slate-500">No event budget created yet.</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!financials?.has_budget && canEdit && (
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={creating}
              data-testid="mini-create-budget-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {creating ? "Creating…" : "Create Budget"}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/crm/events/${eid}/financials`)}
            data-testid="open-full-financials-btn"
          >
            <ExternalLink className="w-4 h-4 mr-1.5" />
            Full Financial Workspace
          </Button>
        </div>
      </div>

      {/* Submitted / pending approval notice (ADR-B12) */}
      {financials?.budget_status === "SUBMITTED" && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-700" data-testid="pending-approval-notice">
          <span className="font-semibold">Pending Approval</span> — Budget submitted.
          Approval governance is part of Wave B3.
        </div>
      )}

      {/* Top 4 KPI cards */}
      {topKpis.length > 0 && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Financial Snapshot</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {topKpis.map(kpi => (
              <FinKpiCard key={kpi.key} kpi={kpi} />
            ))}
          </div>
        </div>
      )}

      {/* No budget empty state */}
      {!financials?.has_budget && (
        <div className="flex flex-col items-center py-10 gap-3 bg-white rounded-xl border border-slate-200 text-slate-400"
          data-testid="no-budget-empty-state">
          <TrendingUp className="w-10 h-10 text-slate-200" />
          <p className="text-sm">Create an event budget to track internal costs against contracted revenue.</p>
          {canEdit && (
            <Button size="sm" onClick={handleCreate} disabled={creating} data-testid="mini-create-budget-empty-btn">
              <Plus className="w-4 h-4 mr-1.5" />
              {creating ? "Creating…" : "Create Budget"}
            </Button>
          )}
        </div>
      )}

      {/* Roadmap note */}
      <div className="text-xs text-slate-400 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
        <span className="font-semibold text-slate-500">B3 Roadmap: </span>
        {financials?.meta?.b3_note || "Approval governance and baseline locking coming in Wave B3."}
      </div>
    </div>
  );
}
